import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as offline from '../utils/offlineAlertStore';
import '../css/login.css';
import '../css/parvekshak.css';

const PER_PAGE = 20;

const TYPE_META = {
  unknown: { label: 'Unknown',  cls: 'badge-unknown' },
  banned:  { label: 'Banned',   cls: 'badge-banned'  },
  instant: { label: 'Detected', cls: 'badge-instant' },
  motion:  { label: 'Motion',   cls: 'badge-motion'  },
};

// Same filename convention as AlertsPage.parseName — kept local so this page
// has no dependency on the (heavy) online AlertsPage module.
function parseName(fname) {
  const base = String(fname).replace(/\.(jpg|jpeg|png|mp4)$/i, '');
  const m    = base.match(/^([a-z]+)_(.+)_(\d{8})_(\d{6})$/i);
  if (m) {
    const [, type, cam, date, time] = m;
    const y = date.slice(0,4), mo = date.slice(4,6), d = date.slice(6,8);
    const h = time.slice(0,2), mi = time.slice(2,4), s = time.slice(4,6);
    return { type, cam, date: `${d}/${mo}/${y}`, time: `${h}:${mi}:${s}` };
  }
  return { type: base.split('_')[0] || 'unknown', cam: null, date: '', time: '' };
}

// Decrypts one cached thumbnail into an object URL and revokes it on unmount.
function CachedThumb({ id, className, onClick }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let alive = true;
    offline.getThumbObjectUrl(id).then(u => {
      if (!alive) { if (u) URL.revokeObjectURL(u); return; }
      url = u; setSrc(u);
    });
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [id]);
  if (!src) return <div className="alert-card-thumb-placeholder">📷</div>;
  return <img src={src} alt={id} className={className} onClick={onClick} />;
}

export default function OfflineAlertsPage() {
  const navigate = useNavigate();
  const supported = offline.isOfflineCacheSupported();

  const [phase,   setPhase]   = useState('checking'); // checking | ready | unavailable
  const [alerts,  setAlerts]  = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(0);
  const [meta,    setMeta]    = useState({ lastSyncedAt: 0 });
  const [lightbox, setLightbox] = useState(null); // { id }
  const busyRef = useRef(false);

  useEffect(() => {
    (async () => {
      if (!supported) return setPhase('unavailable');
      const inited = await offline.isCacheInitialized();
      if (!inited) return setPhase('unavailable');
      setPhase('ready');
    })();
  }, [supported]);

  const loadPage = useCallback(async (p) => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      const { alerts: items, total: t } = await offline.listCachedAlerts({ page: p, perPage: PER_PAGE });
      setAlerts(prev => p === 0 ? items : [...prev, ...items]);
      setTotal(t);
      setPage(p);
      setMeta(await offline.stats());
    } finally { busyRef.current = false; }
  }, []);

  useEffect(() => { if (phase === 'ready') loadPage(0); }, [phase, loadPage]);

  // ── unavailable ───────────────────────────────────────────────────────────
  if (phase === 'unavailable') {
    return (
      <div className="pk-login-root login-page">
        <div className="login-card">
          <div className="login-brand"><div className="login-brand-name">PARVEKSHAK</div></div>
          <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.6 }}>
            {supported
              ? 'No alerts are saved on this device yet. Turn on “Keep recent alerts on this device” in Settings while connected, then they’ll be available here offline.'
              : 'Offline alerts need a secure (https) connection and aren’t available in this mode.'}
          </p>
          <Link to="/local-mode" className="login-btn" style={{ display: 'block', textAlign: 'center', textDecoration: 'none', marginTop: 16 }}>
            ← Back to Local Mode
          </Link>
        </div>
      </div>
    );
  }

  // ── checking ────────────────────────────────────────────────────────────────
  if (phase === 'checking') {
    return (
      <div className="pk-login-root login-page">
        <div className="login-card">
          <div className="login-brand">
            <div className="login-brand-name">PARVEKSHAK</div>
            <div className="login-brand-sub">Saved Alerts</div>
          </div>
          <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>⏳ Checking…</p>
        </div>
      </div>
    );
  }

  // ── read-only cached list ─────────────────────────────────────────────────
  const hasMore = alerts.length < total;
  return (
    <div style={{ height: '100vh', overflowY: 'auto', background: 'var(--bg)' }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 100, padding: '10px 16px',
        background: 'var(--banner-bg, #334155)', color: 'var(--banner-text, #fff)',
        fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
      }}>
        <span>📡 Offline — showing {total} alert{total !== 1 ? 's' : ''} saved on this device</span>
        <button onClick={() => navigate('/login')} style={{
          background: 'rgba(255,255,255,0.15)', color: 'inherit', border: 'none',
          borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
        }}>Sign In</button>
      </div>

      <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-muted)' }}>
        {meta.lastSyncedAt ? `Last saved ${new Date(meta.lastSyncedAt).toLocaleString()}` : ''}
        {' · '}Full images, video and verdicts are available when you’re back online.
      </div>

      {alerts.length === 0 ? (
        <div className="pk-empty">
          <div className="pk-empty-icon">📭</div>
          <h3>No saved alerts</h3>
          <p>Alerts you view while connected are saved here for offline access.</p>
        </div>
      ) : (
        <div className="pk-grid">
          {alerts.map(a => {
            const m  = parseName(a.id);
            const tm = TYPE_META[a.type || m.type] || { label: a.type || m.type, cls: 'badge-instant' };
            return (
              <div key={a.id} className="alert-card">
                <CachedThumb id={a.id} className="alert-card-thumb" onClick={() => setLightbox({ id: a.id })} />
                <div className="alert-card-body">
                  <div className="alert-card-badges">
                    <span className={`badge ${tm.cls}`}>{tm.label}</span>
                    {a.verdict && <span className="badge">{a.verdict}</span>}
                  </div>
                  <p className="alert-card-time">{m.date} {m.time}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasMore && (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <button className="login-btn" style={{ maxWidth: 220 }} onClick={() => loadPage(page + 1)}>Load more</button>
        </div>
      )}

      {lightbox && (
        <div className="pk-lightbox" onClick={() => setLightbox(null)}>
          <button className="pk-lightbox-close" onClick={() => setLightbox(null)}>✕</button>
          <CachedThumb id={lightbox.id} className="pk-lightbox-img" />
        </div>
      )}
    </div>
  );
}
